import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";

import Home from "@/pages/Home";
import Marketplace from "@/pages/Marketplace";
import CourseDetail from "@/pages/CourseDetail";
import Pricing from "@/pages/Pricing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import PracticeEngine from "@/pages/PracticeEngine";
import Results from "@/pages/Results";
import Leaderboard from "@/pages/Leaderboard";
import Wishlist from "@/pages/Wishlist";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import LandingProgrammatic from "@/pages/LandingProgrammatic";
import Categories from "@/pages/Categories";
import Compare from "@/pages/Compare";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import ExitIntentOffer from "@/components/ExitIntentOffer";
import CouponPopup from "@/components/CouponPopup";
import NotFound from "@/pages/NotFound";

import AdminLayout, { AdminOverview } from "@/pages/admin/AdminLayout";
import AdminCourses from "@/pages/admin/AdminCourses";
import AdminQuestions from "@/pages/admin/AdminQuestions";
import AdminCoupons from "@/pages/admin/AdminCoupons";

function AppRouter() {
  const location = useLocation();
  // Handle Emergent OAuth callback (URL fragment) BEFORE any routing
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  // Practice engine and admin & callback views render without site chrome where they want immersion
  const path = location.pathname;
  const isPractice = path.startsWith("/practice/");
  const isAuthScreen = path === "/login" || path === "/register";

  return (
    <>
      {!isPractice && !isAuthScreen && <Header />}
      {!isPractice && !isAuthScreen && <ExitIntentOffer />}
      {!isPractice && !isAuthScreen && <CouponPopup />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/courses/:slug" element={<CourseDetail />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/practice-tests/:slug" element={<LandingProgrammatic />} />
        <Route path="/categories" element={<Categories />} />
        <Route path="/compare" element={<Compare />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />

        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/wishlist" element={<ProtectedRoute><Wishlist /></ProtectedRoute>} />
        <Route path="/practice/:courseId/:testNumber" element={<ProtectedRoute><PracticeEngine /></ProtectedRoute>} />
        <Route path="/results/:attemptId" element={<ProtectedRoute><Results /></ProtectedRoute>} />

        <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminLayout /></ProtectedRoute>}>
          <Route index element={<AdminOverview />} />
          <Route path="courses" element={<AdminCourses />} />
          <Route path="questions" element={<AdminQuestions />} />
          <Route path="coupons" element={<AdminCoupons />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
      {!isPractice && !isAuthScreen && <Footer />}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
